import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  createEmptyApplication,
  getApplicationContent,
  createEmptyFileData,
  updateApplicationContent,
  getApplicationMetadata,
  getPreviousVersionContent,
} from "../../services/Application";
import FileUploadComponent from "./FileUploadComponent";
import HelpTooltip from "./HelpTooltip";
import FileProductsConnector from "./FileProductsConnector";
import FileComponentsConnector from "./FileComponentsConnector";
import InfoPopup from "./InfoPopup";
import { clientCanEdit, operatorCanEdit } from "./ReadOnly";
import ReadonlyOverlay from "./ReadonlyOverlay";
import CardHeader from "./CardHeader";
import ValidationInfo from "./ValidationInfo";
import {
  validateLetters,
  validateDigits,
  validateDecimalNumber,
  validateStep4,
} from "../../utils/Validators";
import LoadingSpinner from "./LoadingSpinner";
import PreviousValue from "./PreviousValue";
import AutoComplete from "./AutoComplete";
import config from "../../config";
import Select from "react-select";
import logger from "../../utils/logger";

const rsStylesCompact = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#F9FBFD",
    minHeight: "calc(1.5em + .75rem + 2px)",
    borderRadius: ".5rem",
    borderColor: state.isFocused ? "#86b7fe" : "#E3EBF6",
    boxShadow: state.isFocused ? "0 0 0 .25rem rgba(13,110,253,.25)" : "none",
    ":hover": { borderColor: state.isFocused ? "#86b7fe" : "#adb5bd" },
    fontSize: "1rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 .75rem" }),
  indicatorsContainer: (base) => ({ ...base, paddingRight: ".25rem" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#e7f1ff"
      : state.isFocused
        ? "#f2f7ff"
        : "white",
    color: "#212529",
    cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, color: "#6c757d" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const ApplicationStep4 = ({ registerCallback, warningsCallback, operator }) => {
  const { accessToken, checkPermission, userId } = useAuth();
  const accessTokenRef = useRef(accessToken);
  const hasAccessToken = Boolean(accessToken);
  let { step, id } = useParams();
  let navigate = useNavigate();
  const location = useLocation();

  const [application, setApplication] = useState(createEmptyApplication());
  const [step4, setStep4] = useState(null);
  const [previousData, setPreviousData] = useState(null);

  const [exportInvoiceFiles, setExportInvoiceFiles] = useState([
    createEmptyFileData(),
  ]);
  const [goodsFiles, setGoodsFiles] = useState([createEmptyFileData()]);
  const [goodsFilesConnections, setGoodsFilesConnections] = useState();
  const [otherGoodsFiles, setOtherGoodsFiles] = useState([
    createEmptyFileData(),
  ]);
  const [otherGoodsFilesConnections, setOtherGoodsFilesConnections] =
    useState();
  const [productFiles, setProductFiles] = useState([createEmptyFileData()]);
  const [productFilesConnections, setProductFilesConnections] = useState([]);
  const [paymentFiles, setPaymentFiles] = useState([createEmptyFileData()]);

  const [goodsList, setGoodsList] = useState([]);
  const [componentsList, setComponentsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [infoPopupTrigger, setInfoPopupTrigger] = useState(0);

  const [exportInvoiceValue, setExportInvoiceValue] = useState(null);
  const [exportInvoiceCurrency, setExportInvoiceCurrency] = useState(null);
  const currencyCodes = [
    "PLN",
    "EUR",
    "USD",
    "CHF",
    "GBP",
    "JPY",
    "AUD",
    "CAD",
    "CNY",
    "SEK",
    "NZD",
    "MXN",
    "SGD",
    "HKD",
    "NOK",
    "KRW",
    "TRY",
    "RUB",
    "INR",
    "BRL",
    "ZAR",
  ];

  const currencyOptions = currencyCodes.map((c) => ({ value: c, label: c }));

  const [status, setStatus] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const [validators, setValidators] = useState({
    exportInvoice: "false",
    exportInvoiceValue: "false",
    exportInvoiceCurrency: "false",
  });

  const [warningPopupTrigger, setWarningPopupTrigger] = useState(0);
  const [errorDescription, setErrorDescription] = useState({
    Errors: [],
    Disclaimer: "",
  });
  const [allowForwardOnError, setAllowForwardOnError] = useState(false);

  const [exportInvoiceDifferences, setExportInvoiceDifferences] =
    useState(null);
  const [goodsFilesDifferences, setGoodsFilesDifferences] = useState(null);
  const [otherGoodsFilesDifferences, setOtherGoodsFilesDifferences] =
    useState(null);
  const [productFilesDifferences, setProductFilesDifferences] = useState(null);
  const [paymentFilesDifferences, setPaymentFilesDifferences] = useState(null);

  const [operatorAnnotations, setOperatorAnnotations] = useState("");

  const showPreviousData = checkPermission("Obsługa wniosków");
  const debug = config.runmode.debug;

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const resetScrollPosition = () => {
    if (typeof document !== "undefined") {
      document.querySelectorAll(".content, .main-content").forEach((element) => {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  const getApplicationNavigationSuffix = () => {
    const params = new URLSearchParams(location.search || "");
    params.set("view", "application");
    params.delete("focus");
    const search = params.toString();
    return `${search ? `?${search}` : ""}${location.hash || ""}`;
  };

  const handleForwardNavigation = () => {
    resetScrollPosition();
    navigate(
      "/application/edit/" +
        (parseInt(step) + 1) +
        "/" +
        id +
        getApplicationNavigationSuffix(),
      {
        replace: true,
      },
    );
  };

  const validateData = async (application, e) => {
    const errorDescription = { Errors: [], Disclaimer: "" };

    let validationInfo = validateStep4(
      application.Step_4.Data,
      application.Step_2.Data,
    );

    if (validationInfo.length > 0) {
      errorDescription.Errors.push({
        Header: `Na bieżącej stronie wniosku stwierdzono następujące błędy (${validationInfo.length}):`,
        Description: validationInfo,
      });
      errorDescription.Disclaimer =
        "Dane wprowadzone we wniosku wymagają poprawy. Możesz kontynuować wypełnianie wniosku, jednak przed jego wysłaniem konieczna będzie korekta.";
      setWarningPopupTrigger(warningPopupTrigger + 1);
    }
    setErrorDescription(errorDescription);

    warningsCallback(3, validationInfo.length);
    if (e.target.name !== "forward") setAllowForwardOnError(false);
    if (validationInfo.length === 0 && e.target.name === "forward")
      handleForwardNavigation();
  };

  const handleSave = async (e) => {
    validateExportInvoice();
    let app = application;
    step4.exportInvoiceFiles = exportInvoiceFiles;
    step4.exportInvoiceValue = exportInvoiceValue;
    step4.exportInvoiceCurrency = exportInvoiceCurrency;
    step4.goodsFiles = goodsFiles;
    step4.goodsFilesConnections = goodsFilesConnections;
    step4.otherGoodsFiles = otherGoodsFiles;
    step4.otherGoodsFilesConnections = otherGoodsFilesConnections;
    step4.productFiles = productFiles;
    step4.productFilesConnections = productFilesConnections;
    step4.paymentFiles = paymentFiles;
    step4.operatorAnnotations =
      operatorAnnotations == "" ? null : operatorAnnotations;

    app.Step_4.Data = step4;
    if (debug) logger.log(JSON.stringify(step4));
    await updateApplicationContent(id, app, userId, accessToken);

    await validateData(app, e);

    setInfoPopupTrigger(infoPopupTrigger + 1);
  };

  const handleForward = async (e) => {
    e.preventDefault();
    setAllowForwardOnError(true);
    validateExportInvoice();
    await handleSave(e);
  };

  const handleBack = async (e) => {
    e.preventDefault();
    resetScrollPosition();
    navigate(
      "/application/edit/" +
        (parseInt(step) - 1) +
        "/" +
        id +
        getApplicationNavigationSuffix(),
      {
        replace: true,
      },
    );
  };

  const validateField = (name, value) => {
    switch (name) {
      case "exportInvoiceValue":
        return validateDecimalNumber(value, 19, true) ? "false" : "true";
      case "exportInvoiceCurrency":
        return validateLetters(value, 3, true) ? "false" : "true";
      default:
        return "true";
    }
  };

  const handleValidate = (e) => {
    const { name, value } = e.target;
    setValidators((prevValidators) => ({
      ...prevValidators,
      [name]: validateField(name, value),
    }));
  };

  const handleFileAdd = (fileType) => {
    const newFile = createEmptyFileData();
    switch (fileType) {
      case "exportInvoiceFiles":
        setExportInvoiceFiles((prevFiles) => [...prevFiles, newFile]);
        break;
      case "goodsFiles":
        setGoodsFiles((prevFiles) => [...prevFiles, newFile]);
        setGoodsFilesConnections((prevFilesConn) => [...prevFilesConn, []]);
        break;
      case "otherGoodsFiles":
        setOtherGoodsFiles((prevFiles) => [...prevFiles, newFile]);
        setOtherGoodsFilesConnections((prevFilesConn) => [
          ...prevFilesConn,
          [],
        ]);
        break;
      case "productFiles":
        setProductFiles((prevFiles) => [...prevFiles, newFile]);
        setProductFilesConnections((prevFilesConn) => [...prevFilesConn, []]);
        break;
      case "paymentFiles":
        setPaymentFiles((prevFiles) => [...prevFiles, newFile]);
        break;
      default:
        break;
    }
  };

  const handleFileChange = (
    id,
    name,
    description,
    fileType,
    index,
    newFile = true,
  ) => {
    const fileInfo = createEmptyFileData();
    fileInfo.id = id;
    fileInfo.name = name;
    fileInfo.description = description;
    if (debug)
      logger.log(
        "S4 hFC: " +
          fileInfo.id +
          ", " +
          fileInfo.name +
          ", " +
          fileInfo.description,
      );

    switch (fileType) {
      case "exportInvoiceFiles":
        setExportInvoiceFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = fileInfo;
          if (newFile) updatedFiles.push(createEmptyFileData());
          return updatedFiles;
        });
        break;
      case "goodsFiles":
        setGoodsFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = fileInfo;
          if (newFile) updatedFiles.push(createEmptyFileData());
          return updatedFiles;
        });
        break;
      case "otherGoodsFiles":
        setOtherGoodsFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = fileInfo;
          if (newFile) updatedFiles.push(createEmptyFileData());
          return updatedFiles;
        });
        break;
      case "productFiles":
        setProductFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = fileInfo;
          if (newFile) updatedFiles.push(createEmptyFileData());
          return updatedFiles;
        });
        break;
      case "paymentFiles":
        setPaymentFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = fileInfo;
          if (newFile) updatedFiles.push(createEmptyFileData());
          return updatedFiles;
        });
        break;
      default:
    }
  };

  const handleFileConnectionChange = (data, fileType, index) => {
    if (debug) logger.log("HFCC");

    switch (fileType) {
      case "goodsFiles":
        setGoodsFilesConnections((prevFilesConn) => {
          const updatedConnections = [...prevFilesConn];
          updatedConnections[index] = data;
          return updatedConnections;
        });
        break;
      case "otherGoodsFiles":
        setOtherGoodsFilesConnections((prevFilesConn) => {
          const updatedConnections = [...prevFilesConn];
          updatedConnections[index] = data;
          return updatedConnections;
        });
        break;
      case "productFiles":
        setProductFilesConnections((prevFilesConn) => {
          const updatedConnections = [...prevFilesConn];
          updatedConnections[index] = data;
          return updatedConnections;
        });
        break;
      default:
    }
  };

  const handleFileRemove = (fileType, index) => {
    if (debug) logger.log("HFR: " + fileType + ", " + index);
    switch (fileType) {
      case "exportInvoiceFiles":
        setExportInvoiceFiles((prevFiles) =>
          prevFiles.filter((_, i) => i !== index),
        );
        break;
      case "goodsFiles":
        setGoodsFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        setGoodsFilesConnections((prevConns) =>
          prevConns.filter((_, i) => i !== index),
        );
        break;
      case "otherGoodsFiles":
        setOtherGoodsFiles((prevFiles) =>
          prevFiles.filter((_, i) => i !== index),
        );
        setOtherGoodsFilesConnections((prevConns) =>
          prevConns.filter((_, i) => i !== index),
        );
        break;
      case "productFiles":
        setProductFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        setProductFilesConnections((prevConns) =>
          prevConns.filter((_, i) => i !== index),
        );
        break;
      case "paymentFiles":
        setPaymentFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        break;
      default:
        break;
    }
  };

  const setApplicationMode = async (metadata = null) => {
    const resolvedMetadata =
      metadata ?? (await getApplicationMetadata(id, accessToken));

    if (!resolvedMetadata) {
      if (debug)
        logger.warn(
          "Brak metadanych wniosku. Pozostawiam tryb edycji bez zmian.",
        );
      return;
    }

    const statusId = Number(resolvedMetadata.status);
    const normalizedStatus = Number.isNaN(statusId)
      ? resolvedMetadata.status
      : statusId;

    setStatus(normalizedStatus);

    const isApplicant = resolvedMetadata.Applicant?.id === userId;
    const useOperatorRules =
      checkPermission("Obsługa wniosków") && !isApplicant;
    const canEdit = useOperatorRules
      ? operatorCanEdit(normalizedStatus)
      : clientCanEdit(normalizedStatus);

    setReadOnly(!canEdit);
  };

  const compareFiles = (currentData, previousData, setHandler) => {
    let differences = [];
    let differencesSummary = "";

    if (previousData)
      differences = previousData.filter(
        (prevInvoice) =>
          !currentData.some((currInvoice) => currInvoice.id === prevInvoice.id),
      );

    if (differences.length > 0)
      differencesSummary =
        "<strong>Usunięte pliki:</strong><br/>" +
        differences.map((file) => "- " + file.name).join(",<br/>");

    differences = currentData
      ? currentData.filter(
          (currInvoice) =>
            !previousData.some(
              (prevInvoice) => prevInvoice.id === currInvoice.id,
            ),
        )
      : [];
    if (differences.length > 0) {
      differencesSummary +=
        (differencesSummary.length > 0 ? "<br/>" : "") +
        "<strong>Dodane pliki:</strong><br/>" +
        differences.map((file) => "- " + file.name).join(",<br/>");
    }

    setHandler(differencesSummary);
  };

  useEffect(() => {
    if (!hasAccessToken) return;
    const fetchData = async () => {
      const token = accessTokenRef.current;
      if (!token) return;

      try {
        setLoading(true);
        if (debug) logger.log("Pobieranie danych");
        const response = await getApplicationContent(id, token);
        const prevApplication = await getPreviousVersionContent(
          id,
          token,
        );
        if (debug) logger.log(response.Step_4);
        if (response.Step_4) {
          setApplication(response);
          setStep4(response.Step_4.Data);
          if (response.Step_4.Data.exportInvoiceFiles)
            setExportInvoiceFiles(response.Step_4.Data.exportInvoiceFiles);
          else setExportInvoiceFiles([createEmptyFileData()]);
          setExportInvoiceValue(
            response.Step_4.Data.exportInvoiceValue
              ? response.Step_4.Data.exportInvoiceValue
              : null,
          );
          setExportInvoiceCurrency(
            response.Step_4.Data.exportInvoiceCurrency
              ? response.Step_4.Data.exportInvoiceCurrency
              : null,
          );

          if (response.Step_4.Data.goodsFiles)
            setGoodsFiles(response.Step_4.Data.goodsFiles);
          else setGoodsFiles([createEmptyFileData()]);

          if (response.Step_4.Data.goodsFilesConnections)
            setGoodsFilesConnections(
              response.Step_4.Data.goodsFilesConnections,
            );
          else setGoodsFilesConnections([]);

          if (response.Step_4.Data.otherGoodsFiles)
            setOtherGoodsFiles(response.Step_4.Data.otherGoodsFiles);
          else setOtherGoodsFiles([createEmptyFileData()]);

          if (response.Step_4.Data.goodsFilesConnections)
            setGoodsFilesConnections(
              response.Step_4.Data.goodsFilesConnections,
            );
          else setGoodsFilesConnections([]);

          if (response.Step_4.Data.otherGoodsFilesConnections)
            setOtherGoodsFilesConnections(
              response.Step_4.Data.otherGoodsFilesConnections,
            );
          else setOtherGoodsFilesConnections([]);

          if (response.Step_4.Data.productFiles)
            setProductFiles(response.Step_4.Data.productFiles);
          else setProductFiles([createEmptyFileData()]);

          if (response.Step_4.Data.productFilesConnections)
            setProductFilesConnections(
              response.Step_4.Data.productFilesConnections,
            );
          else setProductFilesConnections([]);

          if (response.Step_4.Data.paymentFiles)
            setPaymentFiles(response.Step_4.Data.paymentFiles);
          else setPaymentFiles([createEmptyFileData()]);

          if (response.Step_4.Data.operatorAnnotations) {
            setOperatorAnnotations(response.Step_4.Data.operatorAnnotations);
          }
        }

        if (response.Step_2) {
          setGoodsList(
            response.Step_2.Data.Products.filter(
              (product) => product.exporter === "true",
            ),
          );
          setProductsList(
            response.Step_2.Data.Products.filter(
              (product) => product.producer === "true",
            ),
          );
        }

        if (response.Step_3) {
          let tempComponentsList = [];
          response.Step_3.Data.Processes.forEach((process) => {
            process.Products.forEach((product) => {
              product.Components.forEach((component) => {
                tempComponentsList.push(
                  product.name + " > " + component.componentName,
                );
              });
            });
          });
          setComponentsList(tempComponentsList);
        }

        if (prevApplication && prevApplication.Step_4) {
          setPreviousData(prevApplication.Step_4.Data);
          compareFiles(
            response.Step_4.Data.exportInvoiceFiles,
            prevApplication.Step_4.Data.exportInvoiceFiles,
            setExportInvoiceDifferences,
          );
          compareFiles(
            response.Step_4.Data.goodsFiles,
            prevApplication.Step_4.Data.goodsFiles,
            setGoodsFilesDifferences,
          );
          compareFiles(
            response.Step_4.Data.otherGoodsFiles,
            prevApplication.Step_4.Data.otherGoodsFiles,
            setOtherGoodsFilesDifferences,
          );
          compareFiles(
            response.Step_4.Data.productFiles,
            prevApplication.Step_4.Data.productFiles,
            setProductFilesDifferences,
          );
          compareFiles(
            response.Step_4.Data.paymentFiles,
            prevApplication.Step_4.Data.paymentFiles,
            setPaymentFilesDifferences,
          );
        }

        const metadata = await getApplicationMetadata(id, token);
        await setApplicationMode(metadata);
      } catch (error) {
        logger.error("Failed to fetch application content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [hasAccessToken, id]);

  useEffect(() => {
    validateExportInvoice();
  }, [
    exportInvoiceFiles,
    goodsFiles,
    goodsList,
    otherGoodsFiles,
    productFiles,
    productFilesConnections,
    paymentFiles,
  ]);

  const validateExportInvoice = () => {
    let value = "false";
    if (exportInvoiceFiles.length === 1 && exportInvoiceFiles[0].id === "")
      value = "true";

    setValidators((prevValidators) => ({
      ...prevValidators,
      exportInvoice: value,
    }));
  };

  const countWarnings = () => {
    let count = 0;
    for (const key in validators) {
      if (
        validators.hasOwnProperty(key) &&
        (validators[key] === "true" || validators[key] === true)
      ) {
        count++;
      }
    }
    return count;
  };

  useEffect(() => {
    warningsCallback(3, countWarnings());
  }, [validators]);

  return (
    <div className="pb-2">
      <CardHeader
        text="Załączniki obowiązkowe"
        commentId="4-1"
        commentVisible={status !== 1}
        registerCallback={registerCallback}
        viewAll={checkPermission("Obsługa wniosków")}
      />
      <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
        <div className="d-flex flex-column w47">
          <div className="d-flex flex-row">
            <strong>
              Faktura eksportowa, pro-forma, packing list, specyfikacja itp.
              dotyczące wysyłki objętej świadectwem *
            </strong>
            <HelpTooltip
              title='Jeżeli wystawiono więcej niż jeden dokument, załącz wszystkie. Dodatkowo można dołączyć packing list, specyfikację wysyłkową, "Bill of lading” itp.'
              wide="true"
            />
            <ValidationInfo
              title="Załączenie faktury jest obowiązkowe"
              visible={validators.exportInvoice}
            />
          </div>

          <div>
            <PreviousValue
              show={
                showPreviousData &&
                previousData &&
                exportInvoiceDifferences.length > 0 &&
                operator != null
              }
              previousValue={exportInvoiceDifferences}
              header="Wprowadzone zmiany<hr/>"
              left={true}
              wide={true}
            >
              {(exportInvoiceFiles.length === 0 ||
                exportInvoiceFiles[0].id === "") &&
              [true, "true"].includes(readOnly) ? (
                <div className="mt-1">Brak plików</div>
              ) : (
                exportInvoiceFiles.map((file, index) => (
                  <div key={"exportInvoiceFiles_" + file.id} className="mt-1">
                    <FileUploadComponent
                      fileId={file.id}
                      fileName={file.name}
                      fileDescription={file.description}
                      fileType="exportInvoiceFiles"
                      index={index}
                      onChange={handleFileChange}
                      onRemove={() =>
                        handleFileRemove("exportInvoiceFiles", index)
                      }
                      readOnly={readOnly ? "true" : "false"}
                      showReadOnly={false}
                      buttonLabel={
                        index === 0 ? "Dodaj plik" : "Dodaj kolejny plik"
                      }
                    />
                  </div>
                ))
              )}
            </PreviousValue>
          </div>

          {exportInvoiceFiles.length < 1 && (
            <div className="me-auto">
              <button
                type="button"
                className="btn bt</div>n-outline-primary h2 w7 rounded-2"
                onClick={() => {
                  handleFileAdd("exportInvoiceFiles");
                }}
              >
                Dodaj plik
              </button>
            </div>
          )}

          <div className="mt-1">
            <ReadonlyOverlay readonly={readOnly}>
              <div className="d-flex flex-row w47">
                <div className="d-flex flex-column w11">
                  <div className="d-flex flex-row">
                    <label htmlFor="exportInvoiceValue" className="form-label">
                      Wartość eksportu *
                    </label>
                    <ValidationInfo
                      title="Wypełnienie pola jest obowiązkowe. Maksymalna ilość znaków - 15"
                      visible={validators.exportInvoiceValue}
                    />
                  </div>
                  <PreviousValue
                    show={
                      showPreviousData &&
                      previousData &&
                      previousData.exportInvoiceValue !== exportInvoiceValue &&
                      operator != null
                    }
                    previousValue={
                      previousData ? previousData.exportInvoiceValue : null
                    }
                  >
                    <input
                      type="text"
                      className="form-control w11 h2"
                      id="exportInvoiceValue"
                      name="exportInvoiceValue"
                      maxLength="19"
                      value={exportInvoiceValue || ""}
                      onChange={(e) => {
                        const formattedValue = e.target.value
                          .replace(/[^0-9.,]/g, "")
                          .replace(/,/g, ".")
                          .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                          .replace(/(\.\d{2})\d+/, "$1")
                          .replace(/(\..*)\./g, "$1");
                        setExportInvoiceValue(formattedValue);
                      }}
                      onBlur={handleValidate}
                      onFocus={() => validateExportInvoice()}
                      invalid={validators.exportInvoiceValue}
                    />
                  </PreviousValue>
                </div>
                <div className="d-flex flex-column ms-1 w11">
                  <PreviousValue
                    show={
                      showPreviousData &&
                      previousData &&
                      previousData.exportInvoiceCurrency !==
                        exportInvoiceCurrency &&
                      operator != null
                    }
                    previousValue={
                      previousData ? previousData.exportInvoiceCurrency : null
                    }
                  >
                    <div className="d-flex flex-row">
                      <label
                        htmlFor="exportInvoiceCurrency"
                        className="form-label"
                      >
                        Waluta *
                      </label>
                      <ValidationInfo
                        title="Wypełnienie pola jest obowiązkowe. Maksymalna ilość znaków - 3"
                        visible={validators.exportInvoiceCurrency}
                      />
                    </div>
                    <Select
                      classNamePrefix="rs-currency"
                      styles={rsStylesCompact}
                      inputId="exportInvoiceCurrency"
                      options={currencyOptions}
                      value={
                        currencyOptions.find(
                          (o) => o.value === (exportInvoiceCurrency || ""),
                        ) || null
                      }
                      filterOption={(option, raw) =>
                        option.data.value
                          .toLowerCase()
                          .includes(raw.toLowerCase().trim())
                      }
                      placeholder="Wybierz walutę"
                      isClearable
                      components={{ IndicatorSeparator: null }}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      onChange={(opt) => {
                        const val = opt?.value ?? "";
                        setExportInvoiceCurrency(val);
                        handleValidate({
                          target: { name: "exportInvoiceCurrency", value: val },
                        });
                      }}
                    />
                    {false && (
                      <AutoComplete
                        options={currencyCodes}
                        value={exportInvoiceCurrency || ""}
                        invalid={validators.exportInvoiceCurrency}
                        onChange={(e) =>
                          setExportInvoiceCurrency(e.target.value)
                        }
                        onBlur={handleValidate}
                        placeholder={"Wybierz walutę"}
                      />
                    )}
                  </PreviousValue>
                </div>
              </div>
            </ReadonlyOverlay>
          </div>
        </div>
      </div>
      {goodsList.length > 0 && (
        <div>
          <CardHeader
            text="Załączniki do towarów"
            commentId="4-2"
            commentVisible={status !== 1}
            registerCallback={registerCallback}
            viewAll={checkPermission("Obsługa wniosków")}
          />
          <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
            <div className="d-flex flex-column w47">
              <span className="pb-1">
                W poprzednim kroku ustaliliśmy, że <strong>nie jesteś</strong>{" "}
                producentem następujących towarów:
              </span>

              <ul>
                {goodsList.map((goods, index) => (
                  <li key={"goods_" + index}>{goods.productName}</li>
                ))}
              </ul>

              <span className="pt-1">
                Aby ustalić pochodzenie, musisz załadować do systemu{" "}
                <strong>faktury zakupu wskazanych towarów</strong>. Dodając
                każdą fakturę, oznacz towary, których ona dotyczy. Jedna faktura
                może dotyczyć kilku eksportowanych towarów. Jeden eksportowany
                towar mogłeś kupić na podstawie kilku faktur.
              </span>
            </div>

            <div className="mt-1">
              <strong>Załącz faktury zakupu towarów</strong>
            </div>

            <div>
              <PreviousValue
                show={
                  showPreviousData &&
                  previousData &&
                  goodsFilesDifferences.length > 0 &&
                  operator != null
                }
                previousValue={goodsFilesDifferences}
                header="Wprowadzone zmiany<hr/>"
                left={true}
                wide={true}
              >
                {(goodsFiles.length === 0 || goodsFiles[0].id === "") &&
                [true, "true"].includes(readOnly) ? (
                  <div className="mt-1">Brak plików</div>
                ) : (
                  goodsFiles.map((file, index) => (
                    <div
                      className="mt-1 pb-1 border-bottom"
                      key={"goodsFiles_" + file.id}
                    >
                      <FileUploadComponent
                        fileId={file.id}
                        fileName={file.name}
                        fileDescription={file.description}
                        fileType="goodsFiles"
                        index={index}
                        onChange={handleFileChange}
                        onRemove={() => handleFileRemove("goodsFiles", index)}
                        readOnly={readOnly ? "true" : "false"}
                        showReadOnly={false}
                        buttonLabel={
                          index === 0 ? "Dodaj plik" : "Dodaj kolejny plik"
                        }
                      />
                      {goodsList.length > 0 &&
                        ![null, ""].includes(file.id) && (
                          <div className="mt-1">
                            <FileProductsConnector
                              index={index}
                              data={goodsFilesConnections[index]}
                              productsList={goodsList}
                              fileType="goodsFiles"
                              onDataChange={handleFileConnectionChange}
                              readOnly={readOnly ? "true" : "false"}
                            />
                          </div>
                        )}
                    </div>
                  ))
                )}
              </PreviousValue>
            </div>

            {goodsFiles.length < 1 && (
              <div className="me-auto">
                <button
                  type="button"
                  className="btn btn-outline-primary h2 w7 rounded-2"
                  onClick={() => {
                    handleFileAdd("goodsFiles");
                  }}
                >
                  Dodaj plik
                </button>
              </div>
            )}

            <div className="pt-2 d-flex flex-column">
              <strong className="pb-1">
                Inne załączniki, które mogą pomóc przy wystawieniu Świadectwa:
              </strong>

              <ul>
                <li>
                  Dołącz dokumenty potwierdzające pochodzenie, np. świadectwa
                  pochodzenia / deklaracje o kraju pochodzenia / inne dokumenty,
                  na których jest zapis o kraju pochodzenia.
                </li>

                <li>
                  Jeśli zakupiłeś towar na terenie UE - dodatkowo dołącz
                  dokumenty potwierdzające dostawę do Polski na przykład CMR-ki
                </li>
              </ul>
            </div>

            <div>
              <PreviousValue
                show={
                  showPreviousData &&
                  previousData &&
                  otherGoodsFilesDifferences.length > 0 &&
                  operator != null
                }
                previousValue={otherGoodsFilesDifferences}
                header="Wprowadzone zmiany<hr/>"
                left={true}
                wide={true}
              >
                <div>
                  {(otherGoodsFiles.length === 0 ||
                    otherGoodsFiles[0].id === "") &&
                  [true, "true"].includes(readOnly) ? (
                    <div className="mt-1">Brak plików</div>
                  ) : (
                    otherGoodsFiles.map((file, index) => (
                      <div
                        className="mt-1 pb-1 border-bottom"
                        key={"otherGoodsFiles_" + file.id}
                      >
                        <FileUploadComponent
                          fileId={file.id}
                          fileName={file.name}
                          fileDescription={file.description}
                          fileType="otherGoodsFiles"
                          index={index}
                          onChange={handleFileChange}
                          onRemove={() =>
                            handleFileRemove("otherGoodsFiles", index)
                          }
                          readOnly={readOnly ? "true" : "false"}
                          showReadOnly={false}
                          buttonLabel={
                            index === 0 ? "Dodaj plik" : "Dodaj kolejny plik"
                          }
                        />
                        {goodsList.length > 0 &&
                          ![null, ""].includes(file.id) && (
                            <div className="mt-1">
                              <FileProductsConnector
                                index={index}
                                data={otherGoodsFilesConnections[index]}
                                productsList={goodsList}
                                fileType="otherGoodsFiles"
                                onDataChange={handleFileConnectionChange}
                                readOnly={readOnly ? "true" : "false"}
                              />
                            </div>
                          )}
                      </div>
                    ))
                  )}
                </div>
              </PreviousValue>
            </div>
          </div>
        </div>
      )}
      {productsList.length > 0 && (
        <div>
          <CardHeader
            text="Załączniki do produktów"
            commentId="4-3"
            commentVisible={status !== 1}
            registerCallback={registerCallback}
            viewAll={checkPermission("Obsługa wniosków")}
          />
          <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
            <div className="d-flex flex-column w47">
              <span className="pb-1">
                W poprzednim kroku ustaliliśmy, że <strong>jesteś</strong>{" "}
                producentem następujących produktów, które będą podlegały
                eksportowi:
              </span>

              <ul>
                {productsList.map((product, index) => (
                  <li key={"product_" + index}>{product.productName}</li>
                ))}
              </ul>

              <span className="pt-1">
                Aby ustalić pochodzenie, możesz dodać do wniosku dokumenty
                dotyczące surowców, z których wytworzono produkty.
                <br />
                Jeden załącznik może dotyczyć kilku eksportowanych produktów. Do
                jednego produktu możesz dodać kilka załączników.
              </span>

              <span className="py-1">Przykładowe załączniki to:</span>

              <ul>
                <li>kalkulacja kosztów wytworzenia</li>

                <li>
                  faktury zakupu surowców / komponentów użytych w procesie
                  produkcji
                </li>

                <li>
                  dokumenty potwierdzające pochodzenie surowców / komponentów
                  użytych w procesie produkcji np. świadectwa pochodzenia,
                  deklaracje o kraju pochodzenia, inne dokumenty, na których
                  jest zapis o kraju pochodzenia
                </li>

                <li>
                  inne dodatkowe dokumenty i oświadczenia wymagane na etapie
                  weryfikacji
                </li>
              </ul>
            </div>

            <div className="py-1">
              <strong>Załącz dokumenty</strong>
            </div>

            <div>
              <PreviousValue
                show={
                  showPreviousData &&
                  previousData &&
                  productFilesDifferences.length > 0 &&
                  operator != null
                }
                previousValue={productFilesDifferences}
                header="Wprowadzone zmiany<hr/>"
                left={true}
                wide={true}
              >
                <div>
                  {(productFiles.length === 0 || productFiles[0].id === "") &&
                  [true, "true"].includes(readOnly) ? (
                    <div className="mt-1">Brak plików</div>
                  ) : (
                    productFiles.map((file, index) => (
                      <div className="mt-1 pb-1 border-bottom" key={index}>
                        <FileUploadComponent
                          fileId={file.id}
                          fileName={file.name}
                          fileDescription={file.description}
                          fileType="productFiles"
                          index={index}
                          onChange={handleFileChange}
                          onRemove={() =>
                            handleFileRemove("productFiles", index)
                          }
                          readOnly={readOnly ? "true" : "false"}
                          showReadOnly={false}
                          buttonLabel={
                            index === 0 ? "Dodaj plik" : "Dodaj kolejny plik"
                          }
                        />
                        {componentsList.length > 0 &&
                          ![null, ""].includes(file.id) && (
                            <div className="mt-1">
                              <FileComponentsConnector
                                index={index}
                                data={productFilesConnections[index]}
                                componentsList={componentsList}
                                fileType="productFiles"
                                onDataChange={handleFileConnectionChange}
                                readOnly={readOnly ? "true" : "false"}
                              />
                            </div>
                          )}
                      </div>
                    ))
                  )}
                </div>
              </PreviousValue>
            </div>

            {productFiles.length < 1 && (
              <div className="me-auto pb-4">
                <button
                  type="button"
                  className="btn btn-outline-primary py-2 rounded-2 w7"
                  onClick={() => {
                    handleFileAdd("productFiles");
                  }}
                >
                  Dodaj plik
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mb-3">
        <CardHeader
          text="Załączniki - dodatkowe informacje"
          commentId="4-4"
          commentVisible={status !== 1}
          commentLocation="Załączniki - dod. info."
          registerCallback={registerCallback}
          viewAll={checkPermission("Obsługa wniosków")}
        />
        <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
          <div className="d-flex flex-column w47">
            <div className="d-flex flex-row">
              Jeżeli wymagane są dodatkowe zapisy w treści świadectwa, załącz
              dokument, z którego będą one wynikać (np. akredytywa, kontrakt).
              Możesz tutaj dodać także inne załączniki.
            </div>

            {loading ? (
              <LoadingSpinner text="Pobieranie informacji o plikach" />
            ) : (
              <PreviousValue
                show={
                  showPreviousData &&
                  previousData &&
                  paymentFilesDifferences &&
                  paymentFilesDifferences.length > 0 &&
                  operator != null
                }
                previousValue={paymentFilesDifferences}
                header="Wprowadzone zmiany<hr/>"
                left={true}
                wide={true}
              >
                {(paymentFiles.length === 0 || paymentFiles[0].id === "") &&
                [true, "true"].includes(readOnly) ? (
                  <div className="mt-1">Brak plików</div>
                ) : (
                  paymentFiles.map((file, index) => (
                    <div key={"exportInvoiceFiles_" + file.id} className="mt-1">
                      <FileUploadComponent
                        fileId={file.id}
                        fileName={file.name}
                        fileDescription={file.description}
                        fileType="paymentFiles"
                        index={index}
                        onChange={handleFileChange}
                        onRemove={() => handleFileRemove("paymentFiles", index)}
                        readOnly={readOnly ? "true" : "false"}
                        showReadOnly={false}
                        buttonLabel={
                          index === 0 ? "Dodaj plik" : "Dodaj kolejny plik"
                        }
                      />
                    </div>
                  ))
                )}
              </PreviousValue>
            )}
          </div>
        </div>
      </div>

      {checkPermission("Obsługa wniosków") && (
        <div className="mb-3">
          <CardHeader
            text="Uwagi operatora na wniosku"
            commentId="4-5"
            commentVisible={false}
            registerCallback={registerCallback}
            viewAll={true}
          />
          <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
            <div className="d-flex flex-column w47">
              <div className="d-flex flex-row">
                <label htmlFor="operatorAnnotations" className="form-label">
                  Uwagi umieszczone na podpisywanym wniosku PDF
                </label>
              </div>
              <textarea
                className="form-control"
                id="operatorAnnotations"
                name="operatorAnnotations"
                rows="5"
                maxLength="1000"
                value={operatorAnnotations || ""}
                onChange={(e) => setOperatorAnnotations(e.target.value)}
                placeholder="Uwagi na wniosku PDF"
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      )}
      {!readOnly && (
        <div className="flex-column">
          <form>
            <div className="d-flex flex-row ms-4 w55">
              <button
                className="btn btn-outline-primary me-auto w11 h2"
                onClick={handleBack}
              >
                Wstecz
              </button>

              <button
                type="button"
                name="save"
                className="btn btn-outline-primary w11 h2"
                onClick={handleSave}
              >
                Zapisz wersję roboczą
              </button>

              <button
                type="button"
                name="forward"
                className="btn btn-primary ms-1 w11 h2"
                onClick={handleForward}
              >
                Zapisz i przejdź dalej
              </button>
            </div>
          </form>
        </div>
      )}
      <InfoPopup
        title={"Zapisano wersję roboczą wniosku."}
        trigger={infoPopupTrigger}
      />
      <InfoPopup
        title={""}
        errorDescription={errorDescription}
        trigger={warningPopupTrigger}
        errorMode={true}
        actionOnAccept={handleForwardNavigation}
        allowForward={allowForwardOnError}
      />
    </div>
  );
};

export default ApplicationStep4;
